import React, { useState } from "react";
import { Container, ConnectedAlert, ProgressBar } from "../../../../components";
import { Icon, DatePicker, Button, Text, Heading, Link } from "@telia/teddy";
import addDays from "date-fns/addDays";
import format from "date-fns/format";
import nb from "date-fns/locale/nb";
import { connect, useDispatch } from "react-redux";
import { MaybeEmpty } from "../../../../../../constants";
import { RootState } from "../../../../../../models";
import { ExistingSubscription } from "../../../../../../models/transfer";
import { isOrderNotFound as isOrderNotFoundSelector } from "../../../../app/state/selectors";
import RenderVerification from "../../../../checkout/components/organisms/Verification/RenderVerification";
import {
    setTransferDate,
    submitTransferOwnershipOrder,
} from "../../../../checkout/state/actions/transferOwnershipActions";
import {
    sendCode,
    verifyCodeTransferOwnershipExistingCompanyAdmin as verifyCode,
} from "../../../../checkout/state/actions/verificationActions";
import {
    getTransferExistingSubscription,
    isExistingLegalOwnerVerified as isExistingLegalOwnerVerifiedSelector,
    getHasSubmittedTransferOrder,
    getAdminNumber,
    getTransferDate,
} from "../../../../checkout/state/selectors/accountSelectors";
import { getLegalOwnerFullName } from "../../../../checkout/state/selectors/customerInfoSelectors";
import { selectors as LOADING_SELECTORS } from "../../../../loading";
import { getFormattedPhoneNumber } from "../../../../rapidform/formValidation";
import TEXT from "../../../text-consts";

interface StateProps {
    adminNumber: string;
    existingSubscription: MaybeEmpty<ExistingSubscription>;
    hasSubmittedTransferOrder: boolean;
    isExistingLegalOwnerVerified: boolean;
    isFetchingData: boolean;
    isOrderNotFound: boolean;
    legalOwnerFullName: string;
    transferDate?: string;
}

type LandingPageB2BtoB2CProps = StateProps;

const LandingPageB2BtoB2C: React.FC<LandingPageB2BtoB2CProps> = ({
    isFetchingData,
    isOrderNotFound,
    legalOwnerFullName,
    existingSubscription,
    isExistingLegalOwnerVerified,
    hasSubmittedTransferOrder,
    adminNumber,
    transferDate,
}) => {
    const dispatch = useDispatch();

    const [shouldShowVerification, setShouldShowVerification] = useState(false);
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

    if (hasSubmittedTransferOrder) {
        return (
            <div className="tlo-landing-page">
                <div className="tlo-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.LANDING_PAGE.HEADING}
                    </Heading>
                </div>

                <Container size="medium">
                    <div className="tlo-landing-page__receipt">
                        <Icon name="check-circle-filled" className="tlo-landing-page__check-icon" />
                        <Heading mb="100" as="h2" variant="title-200" data-di-mask>
                            {TEXT.LANDING_PAGE.ORDER_CONFIRMATION_HEADER}
                        </Heading>
                        <Text mb="100" data-di-mask>
                            {TEXT.LANDING_PAGE.ORDER_CONFIRMATION_DESCRIPTION(legalOwnerFullName)}
                        </Text>
                        <div className="tlo-landing-page__receipt-back-link">
                            <Link variant="standalone" href="/">
                                <Icon name="arrow-left" />
                                {TEXT.LANDING_PAGE.BACK}
                            </Link>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    if (isOrderNotFound) {
        return (
            <div className="tlo-landing-page">
                <div className="tlo-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.LANDING_PAGE.HEADING}
                    </Heading>
                </div>
                <Container size="medium">
                    <Text mb="100">{TEXT.LANDING_PAGE.ORDER_NOT_FOUND}</Text>
                </Container>
            </div>
        );
    }

    return (
        <div className="tlo-landing-page">
            <div className="tlo-landing-page__heading-container">
                <Heading mb="100" as="h1" variant="title-600">
                    {TEXT.LANDING_PAGE_B2B.HEADING}
                </Heading>
            </div>
            <ProgressBar isFetchingData={isFetchingData} standByColor="light-grey" />
            <ConnectedAlert />
            <Container size="medium">
                <section>
                    <Heading mb="100" as="h2" variant="title-200">
                        {TEXT.LANDING_PAGE_B2B.PIN_CONSENT.HEADING}
                    </Heading>
                    <Text mb="100" data-di-mask>
                        {TEXT.LANDING_PAGE_B2B.REQUEST_RECEIVED(
                            legalOwnerFullName,
                            getFormattedPhoneNumber(existingSubscription.phoneNumber),
                        )}
                    </Text>
                    <Text mb="100">{TEXT.LANDING_PAGE_B2B.DESCRIPTION}</Text>
                </section>
                <section>
                    <Heading mb="100" as="h3" variant="title-200">
                        {isExistingLegalOwnerVerified
                            ? TEXT.LANDING_PAGE_B2B.CHOSEN_DATE.HEADING
                            : TEXT.LANDING_PAGE_B2B.CHOOSE_DATE.HEADING}
                    </Heading>
                    {isExistingLegalOwnerVerified && transferDate ? (
                        <div className="tlo-landing-page__datepicker-container--choosen" data-di-mask>
                            <Text mb="100">{format(new Date(transferDate), "d. MMMM yyyy", { locale: nb })}</Text>
                        </div>
                    ) : (
                        <div className="tlo-landing-page__datepicker-container">
                            <div className="tlo-landing-page__datepicker">
                                <DatePicker
                                    value={transferDate ? format(new Date(transferDate), "yyyy-MM-dd") : ""}
                                    min={format(new Date(), "yyyy-MM-dd")}
                                    max={format(addDays(new Date(), 90), "yyyy-MM-dd")}
                                    onSelectedDate={(date) => dispatch(setTransferDate(date))}
                                    label="Velg dato"
                                />
                            </div>
                            <Text mb="100">{TEXT.LANDING_PAGE_B2B.CHOSEN_DATE.DESCRIPTION}</Text>
                        </div>
                    )}
                </section>
                <section>
                    <Text mb="100" data-di-mask>
                        {TEXT.LANDING_PAGE_B2B.PIN_CONSENT.DESCRIPTION(legalOwnerFullName)}
                    </Text>
                    {!shouldShowVerification && !isExistingLegalOwnerVerified && (
                        <Button
                            type="button"
                            variant="secondary"
                            className="tlo-landing-page__bankid-button"
                            onClick={(): void => setShouldShowVerification(true)}
                        >
                            {TEXT.LANDING_PAGE_B2B.BUTTON.ONE_TIME_PIN}
                        </Button>
                    )}
                    {shouldShowVerification && (
                        <RenderVerification
                            validatePhoneNumber={false}
                            selectedPhoneNumber={adminNumber}
                            sendCode={async (...args): Promise<void> => dispatch(sendCode(...args))}
                            verifyCode={async (...args): Promise<void> => dispatch(verifyCode(...args))}
                            data-di-mask
                        />
                    )}
                </section>
            </Container>
            {isExistingLegalOwnerVerified && (
                <Container className="tlo-landing-page__consent-container" size="medium">
                    <Button
                        type="submit"
                        onClick={async (): Promise<void> => {
                            setHasClickedSubmit(true);
                            dispatch(submitTransferOwnershipOrder());
                        }}
                        disabled={hasClickedSubmit}
                    >
                        {TEXT.LANDING_PAGE_B2B.BUTTON.CONFIRM_TRANSFER}
                    </Button>
                </Container>
            )}
        </div>
    );
};
const mapStateToProps = (state: RootState): StateProps => ({
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    isOrderNotFound: isOrderNotFoundSelector(state),
    legalOwnerFullName: getLegalOwnerFullName(state),
    existingSubscription: getTransferExistingSubscription(state),
    isExistingLegalOwnerVerified: isExistingLegalOwnerVerifiedSelector(state),
    hasSubmittedTransferOrder: getHasSubmittedTransferOrder(state),
    adminNumber: getAdminNumber(state),
    transferDate: getTransferDate(state),
});

export default connect(mapStateToProps)(LandingPageB2BtoB2C);
